import { callGenerateEdgeFunction } from './edgeFunctionClient';

export interface LeaseReviewResult {
  executiveSummary: {
    overallCompliance: 'compliant' | 'non-compliant' | 'needs-review';
    riskLevel: 'low' | 'medium' | 'high' | 'critical';
    summary: string;
    keyFindings: string[];
  };
  detailedFindings: {
    critical: ComplianceIssue[];
    high: ComplianceIssue[];
    medium: ComplianceIssue[];
    low: ComplianceIssue[];
  };
  complianceGaps: {
    missingProvisions: string[];
    outdatedClauses: string[];
    inadequateProtections: string[];
  };
  recommendations: {
    immediate: string[];
    shortTerm: string[];
    longTerm: string[];
  };
  legalConcerns: {
    enforceabilityIssues: string[];
    disputeRisks: string[];
    regulatoryViolations: string[];
  };
  rentalReformBill2025: {
    specificViolations: string[];
    requiredAmendments: string[];
    newRequirements: string[];
  };
  partial?: boolean;
}

export interface ComplianceIssue {
  clause: string;
  issue: string;
  explanation: string;
  recommendation: string;
  legalReference: string;
}

function extractJSON(raw: string): any {
  let cleaned = raw.replace(/```(?:json)?\s*/gi, '').replace(/```\s*/g, '').trim();

  const jsonMatch = cleaned.match(/\{[\s\S]*\}/);
  if (jsonMatch) {
    try {
      return JSON.parse(jsonMatch[0]);
    } catch {}
  }

  try {
    return JSON.parse(cleaned);
  } catch {}

  return null;
}

function buildPartialResult(rawText: string): LeaseReviewResult {
  return {
    executiveSummary: {
      overallCompliance: 'needs-review',
      riskLevel: 'medium',
      summary: rawText.slice(0, 1000),
      keyFindings: ['Analysis completed but structured parsing failed. The raw analysis is shown above.'],
    },
    detailedFindings: { critical: [], high: [], medium: [], low: [] },
    complianceGaps: { missingProvisions: [], outdatedClauses: [], inadequateProtections: [] },
    recommendations: { immediate: [], shortTerm: [], longTerm: [] },
    legalConcerns: { enforceabilityIssues: [], disputeRisks: [], regulatoryViolations: [] },
    rentalReformBill2025: { specificViolations: [], requiredAmendments: [], newRequirements: [] },
    partial: true,
  };
}

const systemPrompt = `You are an expert UK legal document analyzer specializing in rental agreements and housing law compliance, with deep expertise in the Rental Reform Bill 2025.

Your role is to comprehensively analyze rental agreements against current UK housing law and the upcoming Rental Reform Bill 2025 requirements.

**Key Analysis Areas:**

1. **Rental Reform Bill 2025 Compliance:**
   - Abolition of Section 21 'no-fault' evictions
   - New grounds for possession and notice periods
   - Rent increase limitations and procedures
   - Enhanced tenant rights and protections
   - Strengthened property condition standards
   - New landlord registration requirements
   - Updated deposit protection rules

2. **Current UK Housing Law:**
   - Housing Act 1988 compliance
   - Landlord and Tenant Act 1985
   - Consumer Rights Act 2015
   - Homes (Fitness for Human Habitation) Act 2018
   - Tenant Fees Act 2019

3. **Risk Assessment Framework:**
   - **Critical**: Immediate legal violations, unenforceable terms
   - **High**: Significant compliance gaps, dispute risks
   - **Medium**: Outdated provisions, ambiguous terms
   - **Low**: Minor improvements, best practice recommendations

**Analysis Requirements:**
- Identify specific clause violations with legal references
- Assess enforceability under current and future law
- Flag discriminatory or unfair terms
- Evaluate tenant protection adequacy
- Check mandatory disclosure compliance
- Review termination and rent increase procedures

Provide actionable, specific recommendations suitable for legal professionals, landlords, and property managers.`;

function chunkText(text: string, maxChunkSize: number = 3500): string[] {
  const sectionMarkers = /\n(?=SECTION|PART|SCHEDULE|CLAUSE|\d+\.|WHEREAS|NOW THEREFORE|\([a-z]\))/gi;
  let sections = text.split(sectionMarkers);

  const chunks: string[] = [];
  for (const section of sections) {
    if (section.length <= maxChunkSize) {
      chunks.push(section.trim());
    } else {
      const paragraphs = section.split(/\n\n+/);
      let currentChunk = '';

      for (const paragraph of paragraphs) {
        if ((currentChunk + paragraph).length > maxChunkSize) {
          if (currentChunk) chunks.push(currentChunk.trim());
          currentChunk = paragraph;
        } else {
          currentChunk += (currentChunk ? '\n\n' : '') + paragraph;
        }
      }

      if (currentChunk) chunks.push(currentChunk.trim());
    }
  }

  return chunks.filter(chunk => chunk.length > 0);
}

async function analyzeChunk(chunk: string, chunkIndex: number, totalChunks: number): Promise<any> {
  const userPrompt = `Analyze this section (${chunkIndex + 1}/${totalChunks}) of the rental agreement for Rental Reform Bill 2025 compliance and UK housing law violations. Focus on identifying specific issues, legal risks, and required amendments:

${chunk}

Respond with ONLY valid JSON (no markdown code fences, no text before or after). Use this structure:
{
  "issues": [{"clause": "", "issue": "", "riskLevel": "", "legalReference": "", "recommendation": ""}],
  "overallRisk": "low|medium|high|critical",
  "rentalReformConcerns": []
}`;

  const result = await callGenerateEdgeFunction({
    action: 'review',
    systemPrompt,
    userPrompt,
    maxTokens: 2048,
    temperature: 0.2,
  });

  const parsed = extractJSON(result.content);
  if (parsed) return parsed;

  console.warn(`Chunk ${chunkIndex + 1}/${totalChunks} parse failed. Raw (first 500 chars):`, result.content.slice(0, 500));
  return { rawAnalysis: result.content };
}

async function synthesizeAnalysis(chunkAnalyses: any[]): Promise<LeaseReviewResult> {
  const userPrompt = `Combine these rental agreement analyses into a comprehensive compliance report. Respond with ONLY a valid JSON object (no markdown code fences, no text before or after the JSON). Use this exact structure:

{
  "executiveSummary": {
    "overallCompliance": "compliant|non-compliant|needs-review",
    "riskLevel": "low|medium|high|critical",
    "summary": "Brief overall assessment",
    "keyFindings": ["Key finding 1", "Key finding 2"]
  },
  "detailedFindings": {
    "critical": [{"clause": "", "issue": "", "explanation": "", "recommendation": "", "legalReference": ""}],
    "high": [],
    "medium": [],
    "low": []
  },
  "complianceGaps": {
    "missingProvisions": [],
    "outdatedClauses": [],
    "inadequateProtections": []
  },
  "recommendations": {
    "immediate": [],
    "shortTerm": [],
    "longTerm": []
  },
  "legalConcerns": {
    "enforceabilityIssues": [],
    "disputeRisks": [],
    "regulatoryViolations": []
  },
  "rentalReformBill2025": {
    "specificViolations": [],
    "requiredAmendments": [],
    "newRequirements": []
  }
}

Analysis data: ${JSON.stringify(chunkAnalyses)}

IMPORTANT: Return ONLY the JSON object, nothing else.`;

  const result = await callGenerateEdgeFunction({
    action: 'review',
    systemPrompt: 'You must respond with ONLY a valid JSON object matching the exact structure provided. Do not wrap in markdown code fences. Do not include any explanatory text before or after the JSON. Synthesize multiple rental agreement analysis chunks into a comprehensive compliance report. Focus on the most critical issues and provide actionable recommendations organized by priority.',
    userPrompt,
    maxTokens: 8192,
    temperature: 0.2,
  });

  const parsed = extractJSON(result.content);
  if (parsed) return parsed;

  console.warn('Synthesis parse failed. Raw response (first 500 chars):', result.content.slice(0, 500));
  return buildPartialResult(result.content);
}

export async function analyzeLease(leaseContent: string): Promise<LeaseReviewResult> {
  try {
    if (!leaseContent || leaseContent.trim().length === 0) {
      throw new Error('No lease content provided for analysis');
    }

    const chunks = chunkText(leaseContent);

    if (chunks.length === 0) {
      throw new Error('Unable to process lease content - no analyzable sections found');
    }

    const rawChunkAnalyses = await Promise.all(
      chunks.map((chunk, index) => analyzeChunk(chunk, index, chunks.length))
    );

    const chunkAnalyses = rawChunkAnalyses.map((chunk, index) => {
      if (chunk.rawAnalysis && Object.keys(chunk).length === 1) {
        return { issues: [], riskLevel: 'unknown', notes: chunk.rawAnalysis.slice(0, 500), chunkIndex: index };
      }
      return chunk;
    });

    const finalAnalysis = await synthesizeAnalysis(chunkAnalyses);

    if (!finalAnalysis.partial && (!finalAnalysis.executiveSummary || !finalAnalysis.detailedFindings)) {
      throw new Error('Invalid analysis response structure');
    }

    return finalAnalysis;
  } catch (error) {
    console.error('Error analyzing lease:', error);
    throw new Error(`Failed to analyze lease agreement: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}
